import { CheckForAvailableFundsModal } from 'components/order/escrow/escrowCreateEdit/checkForAvailableFundsModal/CheckForAvailableFundsModal';
import { useField } from 'formik';
import React, { useCallback, useState } from 'react';
import { ButtonTW } from 'tailwindComponents/buttonTW/ButtonTW';
import ReactDOM from 'react-dom';
import { ModalTarget } from '../../lineItem/ModalTarget.Constant';
import { useGetEscrowBalancesDataQuery } from 'API/order/escrow/EscrowBaseAPI';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { useSendWireDisbursementMutation } from 'API/order/escrow/WireDisbursementAPI';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { IWireDisbursementFormData } from 'types/order/escrow/wires/IWireDisbursementFormData';
import { Tooltip } from 'ui-toolkit';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useHasPermissions } from '../../../../../accessControl/hooks/useHasPermissions';

export interface ISendWireButtonProps {
  wireDisbursement: IWireDisbursementFormData;
  disabled?: boolean;
}

export const SendWireButton: React.FC<ISendWireButtonProps> = ({ disabled, wireDisbursement }) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);
  const hasDisbursePermissions = useHasPermissions([ePermissions.EditEscrowInitiateDisbursement]);

  const { data } = useGetEscrowBalancesDataQuery(orderId);
  const [onSend] = useSendWireDisbursementMutation();
  const [{ value: amountCents }] = useField<number | undefined>(`value.amountCents`);
  const [{ value: holdback }] = useField<boolean | undefined>(`value.holdback`);

  const hasEnoughMoney = Math.round((data?.availableAmountDollars ?? 0) * 100) >= Math.round(amountCents ?? 0);
  const [showNotEnoughWarning, setShowNotEnoughWarning] = useState<boolean>(false);

  const onSendWireDisbursement = useCallback(async () => {
    const sendResult = await onSend({ wireId: wireDisbursement.id, orderId });
    checkForMutationSuccess(sendResult, 'Sending Wire');
  }, [orderId, wireDisbursement.id, onSend]);

  const onStartSendProcess = useCallback(() => {
    if (hasEnoughMoney) {
      onSendWireDisbursement();
    } else {
      setShowNotEnoughWarning(true);
    }
  }, [hasEnoughMoney, setShowNotEnoughWarning, onSendWireDisbursement]);

  const onIgnoreWarning = useCallback(() => {
    setShowNotEnoughWarning(false);
    onSendWireDisbursement();
  }, [onSendWireDisbursement, setShowNotEnoughWarning]);

  const targetElement = document.getElementById(ModalTarget);

  return (
    <>
      {hasDisbursePermissions && (
        <Tooltip
          placement={'auto'}
          trigger={disabled || holdback ? 'hover' : 'none'}
          tooltipContent={holdback ? 'Cannot send due to holdback' : 'Please save pending changes'}
        >
          <ButtonTW
            type={'button'}
            onClick={onStartSendProcess}
            data-test-id={`send-wire-button`}
            disabled={disabled || holdback}
          >
            Send
          </ButtonTW>
        </Tooltip>
      )}
      {showNotEnoughWarning &&
        targetElement &&
        ReactDOM.createPortal(
          <CheckForAvailableFundsModal
            submitData={onIgnoreWarning}
            type={'wire'}
            handleClose={() => setShowNotEnoughWarning(false)}
          />,
          targetElement
        )}
      {showNotEnoughWarning && !targetElement && (
        <CheckForAvailableFundsModal
          submitData={onIgnoreWarning}
          type={'wire'}
          handleClose={() => setShowNotEnoughWarning(false)}
        />
      )}
    </>
  );
};
